/**
 * Leitura das comissoes da administracao.
 *
 * Rotas futuras (docs/PLANO-PAINEL-COMPLETO.md, "Admin - Comissoes"), so para
 * `platform_admin`; o servidor le `recovery_conversions` (comissao congelada
 * por conversao, ADR-001) e `commission_invoices`:
 *
 *  - `GET /api/admin/commissions?from&to&companyId` -- visao do periodo:
 *    resumo, periodo anterior, serie, empresas, mes corrente.
 *  - `GET /api/admin/commissions/conversions?from&to&companyId&channel&status
 *    &closedBy&refund&amount&q&sort&dir&limit&offset` -- extrato filtrado,
 *    com contagem por filtro (facetas) e soma do recorte.
 *  - `GET /api/admin/commissions/export?<mesmos filtros>` -- o recorte
 *    inteiro para o CSV, com teto (acima dele o servidor gera o arquivo).
 *
 * Dois recursos, e nao um: trocar filtro, ordem ou pagina do extrato nao pode
 * recarregar cartoes, grafico e projecao. As duas respostas saem das mesmas
 * linhas no servidor e por isso batem entre si.
 *
 * `companyId` aqui e um FILTRO da administracao, nao o tenant: a sessao
 * continua decidindo quem ve o que, e o lojista nunca chega nestas rotas. Em
 * modo real, ate as rotas nascerem, a chamada cai em `ErrorPanel` (404) --
 * nunca em numero inventado.
 */

import { fetchOrSimulate, rangeQuery, type Envelope } from '../../lib/api';
import type { CompanyId, PeriodRange } from '../../types';
import { simularExportacao, simularExtrato, simularVisao } from './mock';
import type { CommissionsOverview, ConversionQuery, ConversionsExport, ConversionsResponse } from './types';

export function fetchCommissions(range: PeriodRange, companyId: CompanyId | null, signal: AbortSignal): Promise<Envelope<CommissionsOverview>> {
  return fetchOrSimulate('/api/admin/commissions', { ...rangeQuery(range), companyId }, signal, () => simularVisao(range, companyId));
}

function queryDoExtrato(range: PeriodRange, consulta: ConversionQuery, paginado: boolean): Record<string, string | null> {
  return {
    ...rangeQuery(range),
    companyId: consulta.companyId,
    channel: consulta.channel === 'todos' ? null : consulta.channel,
    status: consulta.status === 'todos' ? null : consulta.status,
    closedBy: consulta.closedBy === 'todos' ? null : consulta.closedBy,
    refund: consulta.refund === 'todos' ? null : consulta.refund,
    amount: consulta.amount === 'todas' ? null : consulta.amount,
    q: consulta.search.trim() === '' ? null : consulta.search.trim(),
    sort: consulta.sort,
    dir: consulta.desc ? 'desc' : 'asc',
    limit: paginado ? String(consulta.limit) : null,
    offset: paginado ? String(consulta.offset) : null,
  };
}

export function fetchConversions(range: PeriodRange, consulta: ConversionQuery, signal: AbortSignal): Promise<Envelope<ConversionsResponse>> {
  return fetchOrSimulate('/api/admin/commissions/conversions', queryDoExtrato(range, consulta, true), signal, () => simularExtrato(range, consulta));
}

export function exportConversions(range: PeriodRange, consulta: ConversionQuery, signal: AbortSignal): Promise<Envelope<ConversionsExport>> {
  return fetchOrSimulate('/api/admin/commissions/export', queryDoExtrato(range, consulta, false), signal, () => simularExportacao(range, consulta));
}
